// src/blogs/BlogShell.jsx
// Shared chrome for all three blog scenes: the floating mini head, the
// left/right edge-nav paging strips, the right-hand filter sidebar, and the
// infinite-scroll feed itself. Each blog scene supplies its own posts, post
// renderer, theme class, mini-head target orientation, and (optionally) a
// blog-specific top section or sidebar extra (movie's poster grid) — this
// component never branches on which blog it's hosting.
import React, { useCallback, useEffect, useMemo, useState } from "react";

import FloatingMiniHead from "./FloatingMiniHead.jsx";
import EdgeNavZones from "./EdgeNavZones.jsx";
import FilterSidebar from "./FilterSidebar.jsx";
import InfiniteBlogFeed from "./InfiniteBlogFeed.jsx";
import PostThemeBackdrop from "./PostThemeBackdrop.jsx";
import { readHashPostId } from "./postLinks.js";
import { ActivePostContext } from "./ActivePostContext.js";
import "./BlogShell.css";

// Maps a post's optional `theme` fields (see the shape note atop
// blogData.js) to the CSS custom properties the blog's own theme file
// (AudioBlog.css etc.) defines its default look with — applied as an inline
// style, so it naturally overrides the class-based defaults only for
// whichever fields the active post's theme actually sets.
function themeToStyleVars(theme) {
  if (!theme) return undefined;
  const style = {};
  if (theme.bg) style["--blog-bg"] = theme.bg;
  if (theme.text) style["--blog-text"] = theme.text;
  if (theme.accent) style["--blog-accent"] = theme.accent;
  if (theme.accentContrast) style["--blog-accent-contrast"] = theme.accentContrast;
  if (theme.surface) style["--blog-surface"] = theme.surface;
  if (theme.border) style["--blog-border"] = theme.border;
  return style;
}

// Matches the breakpoint used elsewhere in the app (see App.jsx `isMobile`
// and HeadScene.jsx's MOBILE_BREAKPOINT).
const MOBILE_BREAKPOINT = 768;

function isNarrowViewport() {
  return typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;
}

export default function BlogShell({
  themeClassName,
  posts,
  renderPost,
  miniHeadTarget,
  onNavigateHome,
  sidebarExtra,
  topSection,
  defaultTheme,
  glassSidebar = false,
  emptyLabel = "posts",
}) {
  // Open by default on desktop, where the floating panel comfortably fits
  // alongside the feed; collapsed by default on mobile, where — even capped
  // in size (see FilterSidebar.css) — an open panel would still cover most
  // of a small screen on first load.
  const [sidebarOpen, setSidebarOpen] = useState(() => !isNarrowViewport());
  const [activeTags, setActiveTags] = useState(() => new Set());
  // The feed's current jump request, { id, instant } — a fresh object per
  // request (see InfiniteBlogFeed). Starts out aimed at the URL's #post-id, if
  // it has one, so a permalink lands on its post on first load.
  const [jumpRequest, setJumpRequest] = useState(() => {
    const id = readHashPostId();
    return id ? { id, instant: true } : null;
  });
  const [activePostId, setActivePostId] = useState(null);

  const toggleTag = useCallback((tag) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const filteredPosts = useMemo(() => {
    if (activeTags.size === 0) return posts;
    return posts.filter((post) => post.tags.some((tag) => activeTags.has(tag)));
  }, [posts, activeTags]);

  // Ask the feed to scroll to a post. If an active tag filter is hiding it,
  // the filter is cleared first — a jump that silently did nothing would be
  // worse than losing the filter.
  const jumpTo = useCallback(
    (id, { instant = false } = {}) => {
      if (!posts.some((post) => post.id === id)) return;
      if (!filteredPosts.some((post) => post.id === id)) setActiveTags(new Set());
      setJumpRequest({ id, instant });
    },
    [posts, filteredPosts]
  );

  // Post links and permalinks move the URL's hash (postLinks.js -> router
  // navigate(), which announces itself as a popstate), and back/forward
  // between hashes fires popstate natively — either way, follow the hash.
  useEffect(() => {
    const onPopState = () => {
      const id = readHashPostId();
      if (id) jumpTo(id);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [jumpTo]);

  // The currently "on screen" post (see InfiniteBlogFeed's intersection
  // observer) drives its own theme override, if it has one — this is the
  // trigger point future per-post themes hook into, nothing else needed.
  const activePost = useMemo(
    () => posts.find((post) => post.id === activePostId) ?? null,
    [posts, activePostId]
  );
  const activeTheme = activePost?.theme;
  const themeStyle = useMemo(() => themeToStyleVars(activeTheme), [activeTheme]);

  // A blog can supply its own always-on `defaultTheme` (same shape as a
  // post's `theme`) — e.g. Thoughts' 3D backdrop. The active post's theme is
  // layered over it, so a post that only overrides an accent keeps the blog's
  // backdrop. Only the backdrop sees the merge; the CSS-variable overrides
  // above stay strictly per-post, since a blog's default colors already live
  // in its own CSS file.
  const backdropTheme = useMemo(
    () => (defaultTheme || activeTheme ? { ...defaultTheme, ...activeTheme } : undefined),
    [defaultTheme, activeTheme]
  );

  const stepPost = useCallback(
    (dir) => {
      if (!filteredPosts.length) return;
      const idx = filteredPosts.findIndex((post) => post.id === activePostId);
      const from = idx === -1 ? 0 : idx;
      const nextIdx = from + dir;
      if (nextIdx < 0 || nextIdx >= filteredPosts.length) return;
      jumpTo(filteredPosts[nextIdx].id);
    },
    [filteredPosts, activePostId, jumpTo]
  );

  return (
    <ActivePostContext.Provider value={activePostId}>
    <div
      className={`blog-shell ${themeClassName} ${
        sidebarOpen ? "blog-shell--sidebar-open" : "blog-shell--sidebar-collapsed"
      }`}
      style={themeStyle}
    >
      <PostThemeBackdrop theme={backdropTheme} post={activePost} />

      <FloatingMiniHead target={miniHeadTarget} onSelect={onNavigateHome} />

      <EdgeNavZones
        onPrev={() => stepPost(-1)}
        onNext={() => stepPost(1)}
        rightDisabled={sidebarOpen}
      />

      <div className="blog-shell__content">
        {topSection}
        <InfiniteBlogFeed
          posts={filteredPosts}
          renderItem={renderPost}
          jumpRequest={jumpRequest}
          onActivePost={setActivePostId}
          emptyLabel={emptyLabel}
        />
      </div>

      <FilterSidebar
        posts={posts}
        activeTags={activeTags}
        onToggleTag={toggleTag}
        onJumpDate={jumpTo}
        open={sidebarOpen}
        onToggleOpen={() => setSidebarOpen((v) => !v)}
        extra={sidebarExtra ? sidebarExtra({ onJumpTo: jumpTo }) : null}
        glass={glassSidebar}
      />
    </div>
    </ActivePostContext.Provider>
  );
}
